import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { formatPrice } from '@/lib/api';
import { Badge } from '@/components/ui/badge';

export default function ProductCard({ product, priority = false }) {
    const { addToCart } = useCart();
    const hasDiscount = product.sale_price && product.sale_price < product.price;
    const discount = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;

    return (
        <div className="group card-lift bg-white rounded-2xl overflow-hidden border border-gray-100/80 hover:border-pink-200/60 hover:shadow-[0_8px_40px_rgba(219,39,119,0.10)] transition-all duration-500 animate-fade-in-up flex flex-col h-full" data-testid={`product-card-${product.id}`}>
            {/* Image - fixed aspect ratio prevents CLS for faster perceived paint */}
            <Link to={`/product/${product.slug}`} className="block relative aspect-square overflow-hidden bg-gradient-to-br from-pink-50/30 to-gray-50">
                <img
                    src={product.images?.[0] || '/placeholder.png'}
                    alt={`${product.name} - ${product.category_name || 'BeautyByChancy'} skincare product`}
                    width={400} height={400}
                    className="w-full h-full object-cover product-card-img"
                    loading={priority ? 'eager' : 'lazy'}
                    fetchPriority={priority ? 'high' : 'low'}
                    decoding="async"
                    sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 220px"
                />
                {hasDiscount && <Badge className="absolute top-2.5 left-2.5 bg-pink-600 text-white text-[10px] rounded-full shadow-lg">{discount}% OFF</Badge>}
                {product.stock <= 0 && <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center"><span className="text-white font-semibold text-sm font-body bg-black/50 px-4 py-1.5 rounded-full">Out of Stock</span></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </Link>
            {/* Content - flex-grow to push button down */}
            <div className="p-3 sm:p-4 flex flex-col flex-1">
                <p className="text-[10px] sm:text-xs text-pink-500/80 font-body mb-1 uppercase tracking-[0.1em] font-medium">{product.category_name}</p>
                <Link to={`/product/${product.slug}`}>
                    <h3 className="text-sm font-medium text-gray-900 font-body line-clamp-2 hover:text-pink-600 transition-colors leading-snug h-[2.6rem]">{product.name}</h3>
                </Link>
                <div className="flex items-center gap-2 mt-auto pt-2">
                    <span className="text-base sm:text-lg font-bold text-pink-600 font-body">{formatPrice(product.sale_price || product.price)}</span>
                    {hasDiscount && <span className="text-[11px] text-gray-400 line-through font-body">{formatPrice(product.price)}</span>}
                </div>
                <button onClick={(e) => { e.preventDefault(); if (product.stock > 0) addToCart(product.id); }}
                    disabled={product.stock <= 0}
                    className="mt-2.5 w-full flex items-center justify-center gap-2 bg-pink-600 text-white py-2.5 rounded-full text-sm font-medium font-body hover:bg-pink-700 hover:shadow-lg hover:shadow-pink-600/20 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]"
                    data-testid={`add-to-cart-${product.id}`}>
                    <ShoppingCart className="w-4 h-4" /> Add to Cart
                </button>
            </div>
        </div>
    );
}
